import { useGetExam, useGetMyCurrentExamPaper } from '@exam-room/graphql';
import { omit } from '@exam-room/utils';
import { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';

import { useGetCookie } from '../../hooks';
import { useConfig } from '../config';
import { useExamCard } from '../exam-card';
import { useExamEvent } from '../exam-event';

import { useGetMyAttachDocument } from './hooks/useGetMyAttachDocument';
import { useGetQuestionsWithImage } from './hooks/useGetQuestionsWithImage';
import {
  type Exam,
  type ExamPaper,
  type QuestionWithOrder,
  type NewExamContextValue,
} from './types';
import { findCurrentExam } from './utils';

interface NewExamProviderProps {
  children: React.ReactNode;
}

export const NewExamContext = createContext<NewExamContextValue | undefined>(undefined);

const EMPTY_QUESTIONS: QuestionWithOrder[] = [];

function NewExamProvider({ children }: Readonly<NewExamProviderProps>) {
  const { cookie } = useConfig();
  const { closestExam, examEvent } = useExamEvent();
  const { examCard } = useExamCard();
  const examCardId = examCard?.id ?? '';
  const [examTime, setExamTime] = useState<{ startedAt: Date | null; endedAt: Date | null } | null>(
    null,
  );
  const [currentExamOrder, setCurrentExamOrder] = useState<number | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  const sessionCookie = useGetCookie(cookie.examSessionCookieName);

  const {
    data: examData,
    loading: isExamLoading,
    error: examError,
    refetch: refetchExam,
  } = useGetExam({
    fetchPolicy: 'network-only',
    skip: !sessionCookie && !(import.meta.env.MODE === 'development'),
    onCompleted: (data) => {
      const exam = findCurrentExam(examEvent, data?.erExamRoomGetExam);
      setCurrentExamOrder(exam?.order ?? null);
      setExamTime(exam ? omit(exam, ['order']) : null);
    },
  });

  const {
    data: examPaperData,
    loading: isExamPaperLoading,
    error: examPaperError,
    refetch: refetchExamPaper,
  } = useGetMyCurrentExamPaper({
    fetchPolicy: 'network-only',
    skip: !sessionCookie && !(import.meta.env.MODE === 'development'),
  });

  const setExamEndTime = useCallback((time: Date) => {
    setExamTime((prev) => ({
      startedAt: prev?.startedAt ?? null,
      endedAt: time,
    }));
  }, []);

  useEffect(() => {
    if (closestExam) {
      setExamTime({
        startedAt: closestExam?.startedAt,
        endedAt: closestExam?.endedAt,
      });
    }
  }, [closestExam]);

  const examPaperItems = examPaperData?.erGetMyCurrentExamPaper.examPaperItems;

  const questions = useMemo(() => {
    return examPaperItems
      ?.map(({ question, order }) => {
        if (!question) return null;
        return {
          ...omit(question, ['__typename']),
          order,
        };
      })
      .filter((item) => item !== null)
      .sort((a, b) => a.order - b.order);
  }, [examPaperItems]);

  const {
    data: parsedQuestions,
    loading: isGetQuestionsWithImageLoading,
    cacheStatus,
    error: questionsWithImageError,
  } = useGetQuestionsWithImage(questions ?? EMPTY_QUESTIONS, closestExam?.examId, refetchKey);

  const {
    url: attachDocumentUrl,
    loading: isAttachDocumentLoading,
    error: attachDocumentError,
  } = useGetMyAttachDocument({
    examCardId,
    refetchKey,
    hasAttachDocument: Boolean(examEvent?.metadata?.attachDocument),
  });

  const refetch = useCallback(async () => {
    await Promise.all([refetchExam(), refetchExamPaper()]);
    setRefetchKey((prev) => prev + 1);
  }, [refetchExam, refetchExamPaper]);

  const questionMap = useMemo(() => {
    const map: Record<string, QuestionWithOrder> = {};
    for (const question of parsedQuestions) {
      map[question.id] = question;
    }
    return map;
  }, [parsedQuestions]);

  const contextValue: NewExamContextValue = useMemo(() => {
    return {
      exam: examData?.erExamRoomGetExam as Exam,
      examPaper: examPaperData?.erGetMyCurrentExamPaper as ExamPaper,
      questions: parsedQuestions,
      questionMap,
      loading:
        isExamLoading ||
        isExamPaperLoading ||
        isGetQuestionsWithImageLoading ||
        isAttachDocumentLoading,
      examTime,
      setExamEndTime,
      currentExamOrder,
      cacheStatus,
      attachDocumentUrl,
      error: examError || examPaperError || questionsWithImageError || attachDocumentError,
      refetch,
    };
  }, [
    examData,
    examPaperData,
    parsedQuestions,
    questionMap,
    isExamLoading,
    isExamPaperLoading,
    isGetQuestionsWithImageLoading,
    examTime,
    setExamEndTime,
    currentExamOrder,
    cacheStatus,
    examError,
    examPaperError,
    questionsWithImageError,
    refetch,
    isAttachDocumentLoading,
    attachDocumentError,
    attachDocumentUrl,
  ]);

  return <NewExamContext.Provider value={contextValue}>{children}</NewExamContext.Provider>;
}

function useNewExam() {
  const context = useContext(NewExamContext);
  if (context === undefined) {
    throw new Error('useNewExam must be used within NewExamProvider');
  }
  return context;
}

export { NewExamProvider, useNewExam };
